<template>
  <!-- Barra de "Conjuntos de hitos y finanzas": pestañas (Contrato principal + subcontratos),
       alta de subcontrato y baja del subcontrato seleccionado. -->
  <div class="sets-bar">
    <span class="sets-label">
      <b-icon pack="fas" icon="layer-group" size="is-small"></b-icon>
      <span>{{ $t('prm_summary_sets_label') }}</span>
    </span>

    <div class="sets-tabs" role="tablist" :aria-label="$t('prm_summary_sets_label')">
      <button v-for="s in tabs" :key="s.id" type="button" role="tab" class="sets-tab"
              :class="{ active: s.id === selectedId }" :aria-selected="s.id === selectedId"
              @click="$emit('select', s.id)">
        {{ s.name }}
      </button>
    </div>

    <button type="button" class="sets-create" @click="openCreate">
      <b-icon pack="fas" icon="plus" size="is-small"></b-icon>
      <span>{{ $t('prm_summary_sets_create') }}</span>
    </button>

    <button v-if="!isMain" type="button" class="sets-remove" @click="removeOpen = true">
      <b-icon pack="fas" icon="trash-can" size="is-small"></b-icon>
      <span>{{ $t('prm_summary_sets_remove') }}</span>
    </button>

    <!-- Alta de subcontrato: nombre + confirmación. -->
    <b-modal :active.sync="createOpen" has-modal-card :can-cancel="false" append-to-body>
      <div class="modal-card">
        <header class="modal-card-head">
          <p class="modal-card-title sets-modal-title">{{ $t('prm_summary_sets_create') }}</p>
        </header>
        <section class="modal-card-body">
          <p class="sets-modal-text">{{ $t('prm_summary_sets_create_desc') }}</p>
          <label class="sets-modal-label" for="finance-set-name">{{ $t('prm_summary_sets_name_label') }}</label>
          <input id="finance-set-name" ref="nameInput" v-model="newName" type="text" maxlength="80"
                 class="sets-modal-input" :class="{ invalid: !!nameError }" :placeholder="$t('prm_summary_sets_name_ph')"
                 @keyup.enter="createSet">
          <p v-if="nameError" class="sets-modal-error">{{ nameError }}</p>
        </section>
        <footer class="modal-card-foot">
          <button type="button" class="button is-primary sets-modal-ok" :disabled="!newName.trim()" @click="createSet">
            {{ $t('prm_summary_sets_create') }}
          </button>
          <button type="button" class="button" @click="createOpen = false">{{ $t('prm_summary_sets_cancel') }}</button>
        </footer>
      </div>
    </b-modal>

    <!-- Baja del subcontrato seleccionado (con aviso de lo que se elimina). -->
    <b-modal :active.sync="removeOpen" has-modal-card :can-cancel="false" append-to-body>
      <div class="modal-card">
        <header class="modal-card-head">
          <p class="modal-card-title sets-modal-title">{{ $t('prm_summary_sets_remove') }}</p>
        </header>
        <section class="modal-card-body">
          <p class="sets-modal-text">{{ removeText }}</p>
        </section>
        <footer class="modal-card-foot">
          <button type="button" class="button sets-modal-danger" :disabled="removing" @click="removeSet">
            {{ removing ? $t('prm_summary_sets_removing') : $t('prm_summary_sets_remove_confirm') }}
          </button>
          <button type="button" class="button" :disabled="removing" @click="removeOpen = false">
            {{ $t('prm_summary_sets_cancel') }}
          </button>
        </footer>
      </div>
    </b-modal>
  </div>
</template>

<script>
  export default {
    name: 'PrmFinanceSetsBar',

    props: {
      sets: { type: Array, default: () => [] },      // subcontratos (el principal no viene aquí)
      selectedId: { type: String, default: 'principal' },
      mainName: { type: String, default: '' },        // etiqueta del contrato principal
    },

    data: () => ({ createOpen: false, removeOpen: false, removing: false, newName: '', nameError: '' }),

    computed: {
      // El contrato principal es siempre la primera pestaña; detrás, los subcontratos.
      tabs () {
        return [{ id: 'principal', name: this.mainName || this.$t('prm_summary_sets_main') }]
          .concat(this.sets.map((s) => ({ id: s.id, name: s.name })))
      },
      isMain () { return this.selectedId === 'principal' },
      selectedSet () { return this.sets.find((s) => s.id === this.selectedId) || null },
      removeText () {
        return this.$t('prm_summary_sets_remove_desc', { name: this.selectedSet ? this.selectedSet.name : '' })
      },
    },

    methods: {
      openCreate () {
        this.newName = ''
        this.nameError = ''
        this.createOpen = true
        this.$nextTick(() => { if (this.$refs.nameInput) this.$refs.nameInput.focus() })
      },

      // Valida el nombre (obligatorio y único) y avisa al padre para que cree el conjunto.
      createSet () {
        const name = this.newName.trim()
        if (!name) {
          this.nameError = this.$t('prm_summary_sets_name_required')
          return
        }
        const exists = this.tabs.some((t) => t.id !== 'principal' && t.name.toLowerCase() === name.toLowerCase())
        if (exists) {
          this.nameError = this.$t('prm_summary_sets_name_taken')
          return
        }
        this.nameError = ''
        this.createOpen = false
        this.$emit('create', name)
      },

      removeSet () {
        if (this.removing || !this.selectedSet) return
        this.removing = true
        this.$emit('remove', this.selectedSet.id)
        this.$nextTick(() => {
          this.removing = false
          this.removeOpen = false
        })
      },
    },
  }
</script>

<style lang="scss" scoped>
  .sets-bar {
    display: flex; flex-wrap: wrap; align-items: center; gap: 6px;
    background: #fff; border: 1px solid #aab6c5; border-radius: 6px;
    box-shadow: inset 3px 0 0 0 #3a6bc8; padding: 6px 10px;
  }
  .sets-label {
    display: inline-flex; align-items: center; gap: 5px;
    font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: .04em; color: #1e3046;
  }
  .sets-tabs { display: flex; flex-wrap: wrap; align-items: center; gap: 4px; }
  .sets-tab {
    border: 1px solid #c9d5e3; background: #f6f8fa; color: #425b76;
    font-size: 10px; font-weight: 600; padding: 4px 9px; border-radius: 4px; cursor: pointer; white-space: nowrap;
  }
  .sets-tab:hover { background: #eaf1f8; }
  .sets-tab.active { background: #3d6b9a; border-color: #3d6b9a; color: #fff; }
  .sets-tab.active:hover { background: #345d86; }

  .sets-create {
    margin-left: auto; display: inline-flex; align-items: center; gap: 5px;
    background: #1e3046; color: #fff; border: 1px solid #1e3046;
    font-size: 10px; font-weight: 600; padding: 5px 10px; border-radius: 4px; cursor: pointer; white-space: nowrap;
  }
  .sets-create:hover { background: #2a3f5a; }
  .sets-remove {
    display: inline-flex; align-items: center; gap: 5px;
    background: #fff; color: #a93226; border: 1px solid #e6b3ad;
    font-size: 10px; font-weight: 600; padding: 5px 10px; border-radius: 4px; cursor: pointer; white-space: nowrap;
  }
  .sets-remove:hover { background: #fdf3f2; }

  // Modales (el contenido va al body: el esc styles es scoped).
  .sets-modal-title { font-size: 14px; font-weight: 700; color: #1e3046; }
  .sets-modal-text { font-size: 11px; line-height: 1.5; color: #425b76; margin-bottom: 10px; }
  .sets-modal-label { display: block; font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: .04em; color: #6b7787; margin-bottom: 4px; }
  .sets-modal-input {
    width: 100%; box-sizing: border-box; border: 1px solid #aab6c5; border-radius: 4px;
    padding: 6px 8px; font-size: 12px; color: #1e3046; outline: none;
  }
  .sets-modal-input:focus { border-color: #3d6b9a; box-shadow: 0 0 0 2px rgba(58, 107, 200, .12); }
  .sets-modal-input.invalid { border-color: #c0392b; }
  .sets-modal-error { font-size: 10px; font-weight: 600; color: #c0392b; margin-top: 4px; }
  .sets-modal-ok { background: #3d6b9a !important; border-color: #3d6b9a !important; }
  .sets-modal-danger { background: #c0392b !important; border-color: #c0392b !important; color: #fff !important; }
</style>
